import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { AppRole } from '@erp-attach/shared';
import { useApiQuery } from '../api/useApiQuery';
import type { MeResponse } from '../api/types';

export interface MeContextValue {
  me: MeResponse | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
  hasRole: (...roles: AppRole[]) => boolean;
  isRecordsAdmin: boolean;
  canAudit: boolean;
}

const MeContext = createContext<MeContextValue | null>(null);

/** Loads /api/me once per session and exposes role helpers. Pages gate on these, the API enforces. */
export function MeProvider({ children, initial }: { children: ReactNode; initial?: MeResponse }) {
  const query = useApiQuery((api, signal) => api.me(signal), [], { enabled: !initial });
  const me = initial ?? query.data;

  const value = useMemo<MeContextValue>(() => {
    const roles = new Set<AppRole>(me?.appRoles ?? []);
    const hasRole = (...wanted: AppRole[]) => wanted.some((r) => roles.has(r));
    return {
      me,
      loading: initial ? false : query.loading,
      error: initial ? null : query.error,
      reload: query.reload,
      hasRole,
      isRecordsAdmin: hasRole('Records.Admin'),
      canAudit: hasRole('Records.Admin', 'Auditor'),
    };
  }, [me, initial, query.loading, query.error, query.reload]);

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

export function useMe(): MeContextValue {
  const ctx = useContext(MeContext);
  if (!ctx) throw new Error('useMe must be used inside <MeProvider>');
  return ctx;
}
